import { Alert, Empty, Input, Modal, Spin } from 'antd'
import { useRolodexEntries } from 'features/rolodex'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import {
  GeckoComponent,
  GeckoUnit,
  RolodexEntryType,
  RooftopFlightDataData,
} from 'types'

import { PhotoExplorer } from './PhotoExplorer'

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${({ theme }) => theme.forecast.background1};
`

const Header = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.background4};
`

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  margin: 0 0 1rem 0;
`

const SearchInput = styled(Input)`
  background-color: ${({ theme }) => theme.forecast.background2};
  border: 1px solid ${({ theme }) => theme.forecast.background4};
  color: ${({ theme }) => theme.forecast.activeText};

  &::placeholder {
    color: ${({ theme }) => theme.forecast.secondaryText};
  }
`

const EntriesListWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`

const EntryCard = styled.div<{ $isNew?: boolean }>`
  padding: 1rem;
  margin-bottom: 0.75rem;
  background-color: ${({ theme, $isNew }) =>
    $isNew
      ? CantileverTheme.colors.SeaGreen + '10'
      : theme.forecast.background2};
  border: 1px solid
    ${({ theme, $isNew }) =>
      $isNew ? CantileverTheme.colors.SeaGreen : theme.forecast.background4};
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
    border-color: ${({ theme }) => theme.forecast.primary};
  }
`

const EntryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 0.5rem;
`

const EntryTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.9375rem;
`

const EntryBadge = styled.div<{ $type: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${({ theme, $type }) => {
    if ($type === 'day-rgb') return theme.forecast.primary + '20'
    if ($type === 'day-ir') return theme.forecast.warning + '20'
    if ($type === 'night-ir') return theme.forecast.danger + '20'
    return theme.forecast.background4
  }};
  color: ${({ theme, $type }) => {
    if ($type === 'day-rgb') return theme.forecast.primary
    if ($type === 'day-ir') return theme.forecast.warning
    if ($type === 'night-ir') return theme.forecast.danger
    return theme.forecast.defaultText
  }};
`

const EntryDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const EntryDetail = styled.div`
  display: flex;
  justify-content: space-between;
`

interface FlightDataEntriesListProps {
  siteId: string | undefined
  newEntryId?: string
}

export const FlightDataEntriesList: React.FC<FlightDataEntriesListProps> = ({
  siteId,
  newEntryId,
}) => {
  const [searchText, setSearchText] = useState('')
  const [selectedEntryId, setSelectedEntryId] = useState<string | undefined>()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { entries, isLoading } = useRolodexEntries(
    siteId
      ? {
          type: [RolodexEntryType.ROOFTOP_FLIGHT_DATA],
          tags: { [RolodexEntryType.SITE]: siteId },
          limit: 10000,
        }
      : []
  )

  const { units, components } = useRolodexEntries(
    siteId
      ? {
          type: [RolodexEntryType.UNIT, RolodexEntryType.COMPONENT],
          tags: { [RolodexEntryType.SITE]: siteId },
          limit: 10000,
        }
      : []
  )

  const unitsById = useMemo(() => {
    return units.reduce<Record<string, GeckoUnit>>((acc, unit) => {
      acc[unit.id] = unit as GeckoUnit
      return acc
    }, {})
  }, [units])

  const componentsById = useMemo(() => {
    return components.reduce<Record<string, GeckoComponent>>(
      (acc, component) => {
        acc[component.id] = component as GeckoComponent
        return acc
      },
      {}
    )
  }, [components])

  const sortedEntries = useMemo(() => {
    return [...entries]
      .map((entry) => ({
        ...entry,
        data: entry.data as RooftopFlightDataData,
      }))
      .sort(
        (a, b) =>
          new Date(b.data.upload_date).getTime() -
          new Date(a.data.upload_date).getTime()
      )
  }, [entries])

  const filteredEntries = useMemo(() => {
    if (!searchText) return sortedEntries

    const lowerSearch = searchText.toLowerCase()
    return sortedEntries.filter(
      (entry) =>
        entry.id.toLowerCase().includes(lowerSearch) ||
        entry.data.flight_set_type.toLowerCase().includes(lowerSearch) ||
        entry.data.weather_type.toLowerCase().includes(lowerSearch) ||
        entry.data.component_id.toLowerCase().includes(lowerSearch)
    )
  }, [sortedEntries, searchText])

  const handleEntryClick = (entryId: string) => {
    setSelectedEntryId(entryId)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedEntryId(undefined)
  }

  const selectedEntry = useMemo(() => {
    return sortedEntries.find((entry) => entry.id === selectedEntryId)
  }, [sortedEntries, selectedEntryId])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatFlightSetType = (type: string) => {
    return type
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const formatWeatherType = (weather: string) => {
    return weather
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return (
    <ListContainer>
      <Header>
        <Title>Flight Data Entries</Title>
        <SearchInput
          placeholder='Search entries...'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </Header>

      <EntriesListWrapper>
        {isLoading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Spin tip='Loading entries...' />
          </div>
        )}

        {!isLoading && filteredEntries.length === 0 && (
          <Empty
            description='No flight data entries found'
            style={{ marginTop: '2rem' }}
          />
        )}

        {!isLoading &&
          filteredEntries.map((entry) => {
            const component = componentsById[entry.data.component_id]
            const asset = component
              ? unitsById[component.tags[RolodexEntryType.UNIT] || '']
              : undefined

            return (
              <EntryCard
                key={entry.id}
                $isNew={entry.id === newEntryId}
                onClick={() => handleEntryClick(entry.id)}>
                <EntryHeader>
                  <EntryTitle>
                    {asset?.data.name || 'Unknown Asset'} /{' '}
                    {component?.data.name || 'Unknown Component'}
                  </EntryTitle>
                  <EntryBadge $type={entry.data.flight_set_type}>
                    {formatFlightSetType(entry.data.flight_set_type)}
                  </EntryBadge>
                </EntryHeader>

                <EntryDetails>
                  <EntryDetail>
                    <span>Photos:</span>
                    <span>
                      {entry.data.uploaded_photos.length} /{' '}
                      {entry.data.total_photos}
                    </span>
                  </EntryDetail>
                  <EntryDetail>
                    <span>Weather:</span>
                    <span>{formatWeatherType(entry.data.weather_type)}</span>
                  </EntryDetail>
                  <EntryDetail>
                    <span>Uploaded:</span>
                    <span>{formatDate(entry.data.upload_date)}</span>
                  </EntryDetail>
                </EntryDetails>

                {entry.id === newEntryId && (
                  <Alert
                    title='Just uploaded'
                    type='success'
                    showIcon
                    style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}
                  />
                )}
              </EntryCard>
            )
          })}
      </EntriesListWrapper>

      <Modal
        title={
          selectedEntry
            ? `Flight Data: ${formatFlightSetType(selectedEntry.data.flight_set_type)} - ${selectedEntry.data.component_id.slice(0, 20)}...`
            : 'Flight Data Photos'
        }
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width='90%'
        style={{ top: 20 }}>
        {selectedEntry && (
          <PhotoExplorer
            entryId={selectedEntry.id}
            photoFilenames={selectedEntry.data.uploaded_photos}
          />
        )}
      </Modal>
    </ListContainer>
  )
}
